const PrivacyPolicy = () => {
  return (
    <div className="content-page">
      <div className="mb-4">
        <i className="fa-sharp fa-solid fa-file font-xl mr-11" />
        <span className="page-title ng-scope">Privacy policy</span>
      </div>
      <p>
        <strong>Effective 4 September 2026</strong>
      </p>
      <p>
        ICN uses personal information to administer global membership,
        competitions, sporting records and anti-doping. We do not sell or rent
        personal information. Certain competition and integrity records form
        part of ICN's long-term sporting history and may remain after an account
        is closed. Health and anti-doping material is handled with additional
        care and is shared only where reasonably necessary or legally permitted.
      </p>
      <h3>1. About this Policy</h3>
      <p>
        This Privacy Policy explains how Natural Icons Pty. Ltd. (ABN 52 089 094
        351), trading as iCompete Natural (“ICN”, “we”, “us” or “our”),
        collects, holds, uses, discloses and protects personal information. It
        applies to the ICN website and member portal, membership, competition
        and event administration, anti-doping activities, communications and
        related services.
      </p>
      <p>
        ICN is based in Victoria, Australia and operates internationally. We
        handle personal information consistently with the Privacy Act 1988 (Cth)
        and the Australian Privacy Principles where they apply, and with other
        applicable privacy laws. Where ICN determines why and how personal data
        is processed, Natural Icons Pty. Ltd. is the data controller or
        equivalent responsible organisation.
      </p>
      <p>
        This Policy is a notice about our practices; it is not a request for
        blanket consent. When a law requires consent for a particular activity,
        such as collection of sensitive information, optional marketing or
        certain cookies, we will request that consent separately.
      </p>
      <h3>2. When another organisation is responsible</h3>
      <p>
        ICN works with country representatives, independent event promoters,
        venues, laboratories, sample-collection personnel, photographers and
        other providers. Some act on ICN's instructions. Others independently
        decide how they handle information for their own event, business or
        legal purposes and are responsible for their own privacy practices. Any
        separate collection notice or privacy policy they provide also applies
        to their handling.
      </p>
      <p>
        External websites, social-media platforms and payment services linked
        from the portal may collect information under their own privacy
        policies. This Policy covers ICN's handling of information, not an
        independent third party's separate activities.
      </p>
      <h3>3. Personal information we collect</h3>
      <p>
        Depending on how a person interacts with ICN, we may collect the
        following categories:
      </p>
      <ul>
        <li>
          Identity and contact details, such as name, date of birth, age, sex or
          gender where relevant to divisions, country or nationality, address,
          email address, telephone number, profile photograph, member number and
          identity-verification information.
        </li>
        <li>
          Account and membership details, such as login and security
          information, membership type, start and expiry dates, renewal status,
          member roles and status icons, guardian details and approval for a
          minor, and records of account closure, suspension or termination.
        </li>
        <li>
          Sporting and event information, such as event applications and
          entries, divisions, relevant measurements, represented country,
          attendance, qualifications, placings, rankings, awards, professional
          or official status, eligibility decisions, appeals and disciplinary
          history.
        </li>
        <li>
          Payment and transaction information, such as billing contact details,
          amount, currency, payment status, transaction reference, renewal,
          refund and chargeback records. Payment providers generally receive
          full card, bank or digital-payment credentials; ICN does not
          ordinarily store the complete card number or security code.
        </li>
        <li>
          Anti-doping and health information, such as declarations, relevant
          medical conditions and medications, Therapeutic Use Exemption
          material, prior substance-use information, sample-collection and
          chain-of-custody records, coded sample identifiers, laboratory
          analysis, test results, eligibility findings and sanctions.
        </li>
        <li>
          Portal content and communications, such as profile content,
          photographs, documents, messages or submissions to ICN, support
          requests, complaints, reports of suspected misconduct, responses, and
          communications with event or anti-doping personnel.
        </li>
        <li>
          Technical and usage information, such as IP address, browser and
          device information, approximate location derived from an IP address,
          session and cookie identifiers, pages and functions used, referring
          page, and successful or unsuccessful access and security records.
        </li>
      </ul>
      <p>
        If you provide personal information about another person, such as a
        parent, guardian, coach or emergency contact, you must have authority to
        do so and should make this Policy available to that person.
      </p>
      <h3>4. How we collect information</h3>
      <p>We may collect information:</p>
      <ul>
        <li>
          directly from you when you create or update an account, buy or renew
          membership, enter an event, submit a form or document, contact us,
          make a payment, attend an event or participate in testing;
        </li>
        <li>from an authorised parent, guardian or representative;</li>
        <li>
          from ICN country representatives, event promoters, officials, sample
          collectors, laboratories, medical or TUE reviewers, photographers,
          payment providers and service providers;
        </li>
        <li>
          from another sporting body, complainant, witness, publicly available
          sporting record or other lawful source where relevant to eligibility,
          integrity, safety or an investigation; and
        </li>
        <li>
          automatically through the portal's logs, cookies and similar
          technologies.
        </li>
      </ul>
      <p>
        We seek to collect information directly from the person concerned where
        reasonable and practicable. If we receive unsolicited personal
        information, we assess whether ICN could lawfully have collected it and
        will retain, use, de-identify or securely dispose of it as appropriate.
      </p>
      <h3>5. Why we collect, use and disclose information</h3>
      <p>ICN handles personal information where reasonably necessary to:</p>
      <ul>
        <li>create, secure and support accounts and administer memberships;</li>
        <li>
          receive and assess competition applications, allocate divisions,
          confirm qualifications and eligibility, and administer events;
        </li>
        <li>
          operate anti-doping, TUE, testing, results-management, appeal and
          disciplinary processes and protect fair competition;
        </li>
        <li>
          maintain and publish member status, competition entries, results,
          rankings, awards and historical sporting records;
        </li>
        <li>
          process payments, renewals, refunds and chargebacks and maintain
          financial and audit records;
        </li>
        <li>
          send account, membership, event, payment, testing, policy, security
          and other service communications;
        </li>
        <li>
          respond to enquiries, complaints and reports; enforce ICN rules and
          the Code of Ethics; and protect people, the portal and ICN from
          misuse, fraud, security threats or unlawful activity;
        </li>
        <li>
          operate, troubleshoot, secure, analyse and improve the portal and ICN
          services; and
        </li>
        <li>
          meet legal, insurance, recordkeeping, governance and
          dispute-resolution obligations and establish, exercise or defend legal
          claims.
        </li>
      </ul>
      <p>
        If required information is not provided, ICN may be unable to create or
        secure an account, process membership or payment, assess an event
        application or eligibility, administer testing, respond to a request or
        provide another requested service. We will not use information for a
        materially different, incompatible purpose without taking any notice or
        consent steps required by law.
      </p>
      <h3>6. Legal bases for international members</h3>
      <p>
        Where an applicable law requires ICN to identify a legal basis for
        processing, we rely as appropriate on performance of a contract or steps
        requested before a contract; compliance with legal obligations; ICN's
        and the sporting community's legitimate interests in administering safe
        and fair competitions, membership, security and historical records;
        consent; protection of vital interests; and the establishment, exercise
        or defence of legal claims.
      </p>
      <p>
        For health and other sensitive information, we rely on explicit consent
        or another condition permitted by applicable law. A person may withdraw
        consent prospectively, but this does not affect prior lawful handling
        and may mean ICN cannot determine eligibility or permit participation
        where the information is genuinely required.
      </p>
      <h3>7. Sensitive, health and anti-doping information</h3>
      <p>
        ICN collects sensitive information only where it is reasonably necessary
        for its activities and the person has consented, or where another legal
        permission applies. Anti-doping and health information is used for
        purposes such as eligibility, TUE assessment, sample collection and
        analysis, results management, appeals, enforcement and the integrity and
        safety of natural competition.
      </p>
      <p>
        Access is limited to authorised people who need the information for
        those purposes, which may include designated ICN officials, event
        personnel, sample collectors, laboratories, medical or TUE reviewers and
        sporting decision-makers. Samples and related records may use coded
        identifiers where practical.
      </p>
      <p>
        ICN does not ordinarily publish medical documents, TUE applications or
        full laboratory paperwork. A test status or outcome, eligibility
        decision, sanction or disciplinary outcome may be recorded or published
        where permitted by the applicable ICN policy, procedural fairness
        requirements and law.
      </p>
      <h3>8. Profiles, results and other public information</h3>
      <p>
        ICN is a sporting organisation and some records are intended to be
        visible to members or the public. Depending on the portal function,
        event and profile settings, these may include a person's name,
        photograph, country, member number, member or official status, event
        participation, division, placing, result, ranking, qualification and
        award. Event photographs, video and reports may also identify
        participants.
      </p>
      <p>
        Public sporting records may remain available as part of ICN's historical
        archive even after membership ends or an account is closed. Public
        material may be indexed by search engines or copied and archived by
        third parties; ICN cannot always remove copies outside its control. ICN
        does not intentionally make a member's private contact details public
        unless the member chooses to publish them or another lawful basis
        applies.
      </p>
      <h3>9. Who we disclose information to</h3>
      <p>ICN may disclose relevant information to:</p>
      <ul>
        <li>
          authorised ICN personnel, country representatives, event promoters,
          officials and volunteers who need it to administer membership, events,
          rules, safety or integrity;
        </li>
        <li>
          sample-collection personnel, laboratories, medical or TUE reviewers,
          hearing or appeal personnel and other authorised anti-doping
          decision-makers;
        </li>
        <li>
          payment processors, banks and financial-service providers for
          transactions, refunds, fraud prevention and chargebacks;
        </li>
        <li>
          technology, hosting, cloud-storage, backup, cybersecurity, email,
          messaging, support and analytics providers that support ICN
          operations;
        </li>
        <li>
          professional advisers, auditors, insurers and contractors subject to
          appropriate duties;
        </li>
        <li>
          regulators, courts, tribunals, law-enforcement agencies or other
          persons where required or authorised by law, necessary to respond to a
          serious threat, or reasonably necessary for a legal claim or
          investigation; and
        </li>
        <li>
          a successor or proposed successor in connection with a genuine
          corporate restructure, sale or transfer, subject to appropriate
          confidentiality and legal requirements.
        </li>
      </ul>
      <p>
        ICN does not sell or rent personal information and does not provide
        identifiable member lists to sponsors or advertisers. We may publish
        sporting records as described above, disclose information with the
        person's consent, or share aggregated or de-identified statistics that
        do not reasonably identify an individual.
      </p>
      <h3>10. Overseas handling and disclosure</h3>
      <p>
        ICN's membership and events are international, public portal information
        is accessible globally, and some administrators and service providers
        are located outside the person's home country. Likely recipient
        locations include Australia, India and the United States, as well as the
        country where a relevant event, promoter, ICN representative, official,
        participant or specialist provider is located. Because ICN operates
        across many countries, it is not practicable to list every possible
        event location in a static policy. You may contact us for more specific
        information about a particular disclosure.
      </p>
      <p>
        Examples include portal and technical support in India, drug-testing
        laboratory services in the United States, payment or cloud services that
        may process information internationally, and event administration in the
        country of competition. Privacy protections and lawful government-access
        rights differ between countries.
      </p>
      <p>
        Before disclosing personal information overseas, ICN takes reasonable
        steps required by applicable law, which may include limiting the
        information disclosed, access controls, due diligence and contractual
        privacy and security requirements. Where EU, EEA or UK transfer rules
        apply, ICN will use an approved contractual or other lawful transfer
        mechanism where required.
      </p>
      <h3>11. Payments</h3>
      <p>
        Membership and other payments are processed through third-party payment
        providers. The provider shown at checkout may collect payment
        credentials, identity, billing, device and transaction information under
        its own privacy policy and legal obligations. ICN generally receives a
        transaction reference, payer and contact details, amount, currency,
        status and information needed to reconcile a payment, renewal, refund or
        dispute.
      </p>
      <h3>12. Cookies, logs and analytics</h3>
      <p>
        The portal uses cookies and similar technologies where needed for
        sign-in, session continuity, preferences, load management, security and
        fraud prevention. ICN may also use limited analytics to understand use
        of the website, measure performance, diagnose errors and improve
        services. These technologies may collect identifiers, IP address, device
        and browser information, referring page and interactions with portal
        functions.
      </p>
      <p>
        A browser may allow cookies to be blocked or deleted, but doing so can
        prevent sign-in or other portal functions from working. If ICN deploys
        non-essential advertising or campaign-measurement technologies, we will
        provide any separate notice and choice required by law. ICN does not use
        medical, TUE or anti-doping information for behavioural advertising.
      </p>
      <h3>13. Service messages and marketing</h3>
      <p>
        ICN may send non-marketing service messages reasonably necessary for an
        account, membership, payment, event entry, testing request, policy,
        safety or security matter. Members should keep their contact details
        current.
      </p>
      <p>
        ICN sends marketing only with the consent or other permission required
        by applicable law. A recipient may unsubscribe using the method in the
        message or by contacting us. An opt-out from marketing does not prevent
        necessary service messages. ICN does not disclose contact details to an
        advertiser for the advertiser's own marketing without consent.
      </p>
      <h3>14. Computer-assisted and automated processing</h3>
      <p>
        Computer programs may use account, membership, event, payment and
        technical information to perform routine tasks, such as validating
        fields, calculating membership status or expiry, displaying status
        indicators, detecting unusual access or transactions, and routing a
        matter for review. These tasks may inform an administrator but do not
        replace the applicable ICN rules or procedural safeguards.
      </p>
      <p>
        ICN does not currently use a computer program to make, without
        meaningful human involvement, a final decision that could reasonably be
        expected to significantly affect a person's rights or interests,
        including a final anti-doping, disciplinary, competition-eligibility,
        sanction, membership-termination or appeal decision. A person may
        contact ICN to request information or human review if they believe an
        automated process has materially affected them.
      </p>
      <h3>15. Retention and account closure</h3>
      <p>
        ICN keeps personal information only for as long as reasonably needed for
        the purposes described in this Policy, subject to legal, financial,
        insurance, dispute and backup requirements. Retention depends on the
        type of record and its continuing purpose.
      </p>
      <ul>
        <li>
          Account and membership information is generally kept while an account
          is active and for a reasonable period afterwards for administration,
          security, complaints and legal claims.
        </li>
        <li>
          Payment, refund and accounting records are kept for the period
          required by tax, corporate, payment and other applicable laws.
        </li>
        <li>
          Competition participation, results, placings, rankings, awards,
          qualifications and related sporting records may be retained
          indefinitely as ICN's historical sporting record.
        </li>
        <li>
          Anti-doping, TUE, eligibility, appeal and disciplinary records may be
          kept for as long as reasonably necessary to apply the rules, preserve
          sporting integrity, verify eligibility or sanctions, resolve disputes
          and meet legal obligations.
        </li>
        <li>
          Routine technical logs and support material are deleted or
          de-identified when no longer reasonably needed, subject to security,
          investigation and backup cycles.
        </li>
      </ul>
      <p>
        When a member requests account closure, ICN will deactivate access and
        delete, de-identify or restrict non-essential profile and account
        information where appropriate. Closure does not require ICN to erase
        historical event results, drug-testing, eligibility, disciplinary,
        financial or other records that ICN reasonably needs or is legally
        entitled or required to retain. Residual copies may remain in protected
        backups until they are overwritten in the ordinary cycle.
      </p>
      <h3>16. Security and data breaches</h3>
      <p>
        ICN uses administrative, technical and physical safeguards appropriate
        to the nature and sensitivity of the information. These include
        restricting access to people with a genuine role, account and access
        controls, security logging, provider controls, and secure storage or
        transmission measures where appropriate. No online service or storage
        method can be guaranteed completely secure.
      </p>
      <p>
        If ICN becomes aware of a personal-information breach, it will assess
        and contain the incident and notify affected people and regulators where
        required by applicable law. Members should use a unique password,
        protect their login details and promptly report suspected unauthorised
        access.
      </p>
      <h3>17. Young people</h3>
      <p>
        ICN Premium Membership and competition participation are intended for
        people aged 15 years and over, subject to the applicable division, event
        rules and local law. A parent or legal guardian must approve membership
        and participation for a person under 18, and may need to provide or
        approve personal and sensitive information. If ICN learns that an
        account for a child under 15 was created without appropriate authority,
        we will investigate and take appropriate action.
      </p>
      <h3>18. Your privacy rights</h3>
      <p>
        Subject to applicable law, a person may ask ICN to provide access to
        personal information, correct inaccurate or incomplete information,
        delete or de-identify information, restrict or object to processing,
        provide eligible data in a portable form, or withdraw a consent. A
        person may always opt out of direct marketing.
      </p>
      <p>
        ICN may need to verify identity or authority before acting. A right may
        be limited where an exception applies or where retention is necessary
        for legal obligations, public and historical sporting records,
        anti-doping or disciplinary integrity, procedural fairness, security,
        another person's rights or legal claims. If ICN cannot fulfil a request,
        we will explain why where required. A privacy correction request does
        not itself overturn a sporting decision; the applicable review or appeal
        process must be used for that decision.
      </p>
      <p>
        People in the EU, EEA, UK and other jurisdictions may have additional
        local rights and may complain to their local data-protection authority.
        Withdrawal of consent does not affect processing already carried out
        lawfully. ICN will respond within the period required by applicable law
        and does not ordinarily charge for a reasonable privacy request.
      </p>
      <h3>19. Contacting ICN and making a complaint</h3>
      <p>
        <strong>Privacy contact:</strong> Privacy Officer, Natural Icons Pty.
        Ltd. trading as iCompete Natural
      </p>
      <p>
        <strong>Email:</strong>{" "}
        <a href="mailto:admin@icompetenatural.com">admin@icompetenatural.com</a>
      </p>
      <p>
        <strong>Online:</strong>{" "}
        <a href="https://www.icompetenatural.com/contact">ICN Help Centre</a>
      </p>
      <p>
        Please describe the issue and the outcome requested. ICN may ask for
        reasonable identity information and will investigate and respond within
        a reasonable period. If a person is not satisfied with ICN's response,
        they may be entitled to complain to the Office of the Australian
        Information Commissioner or the privacy regulator in their country or
        region.
      </p>
      <p>
        <strong>Australian regulator:</strong>{" "}
        <a href="https://www.oaic.gov.au/privacy/privacy-complaints">
          Office of the Australian Information Commissioner
        </a>
      </p>
      <h3>20. Changes to this Policy</h3>
      <p>
        ICN may update this Policy to reflect changes in law, technology,
        providers or its services and practices. The current version and
        effective date will be published on the ICN website. Where a change
        materially affects how existing personal information is handled, ICN
        will take reasonable steps to provide additional notice or obtain
        consent if required.
      </p>
      <p>
        <strong>
          Natural Icons Pty. Ltd. | ABN 52 089 094 351 | trading as iCompete
          Natural
        </strong>
      </p>
    </div>
  );
};

export default PrivacyPolicy;
